import { useState, useEffect, useContext, useRef } from "react";
import SettingsContext from "@serie3/common/auth/SettingsContext";
import { canAccessAllPlatforms } from "@serie3/common/config/RoleRights";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import SocialEndpoints from "@serie3/mymediaplanner/Modules/Social/endpoints";
import crudApi from "@serie3/common/api/crudApi";
import CustomEvent from "./components/CustomEvent";
import NewModal from "../shared/Modals/NewModal";
import PostPreviewPanel from "./components/Previews/PostPreviewPanel";
import useMyDisplayName from "../shared/useMyDisplayName";
import WithdrawApprovalButton, { canWithdraw } from "./components/Approvals/WithdrawApprovalButton";
import { DecideButtons } from "./components/Approvals/ApprovalScheduleList";
import { scheduleIsForMe } from "@serie3/mymediaplanner/Modules/Social/Domain/delegation";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWandMagicSparkles } from "@fortawesome/free-solid-svg-icons";
import DocumentsEndpoints from "@serie3/mymediaplanner/Modules/Documents/endpoints";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import Schedule from "@serie3/mymediaplanner/Modules/Social/Entity/Schedule";
import ScheduleForm from "@serie3/mymediaplanner/Modules/Social/Forms/ScheduleForm";
import { useFormik } from "formik";
import ScheduleFormView from "./components/Schedules/ScheduleFormView";
import PostFormView from "./components/PostFormView";
import { Row, Col, Button } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import listHandler from "@serie3/common/Domain/listHandler";
import AiPlanningAssistantView from "./components/SubComponents/AiPlanningAssistantView";
import setCalendarEntry from "@serie3/mymediaplanner/Modules/Calendar/Domain/CreateCalendarEntries";

// Setup moment for date localization
const localizer = momentLocalizer(moment);

const PostCalendar = ({
  schedules = [],
  setSchedules,
  dontUseCalendarClick = false,
  customDeleteFunction = null,
  savePlan = null,
  date,
  onMonthChange,
  onPlanSaved,
}) => {
  const { t } = useTranslation();
  const { settings } = useContext(SettingsContext);
  const allPlatforms = canAccessAllPlatforms(settings?.user?.roles);
  const [events, setEvents] = useState([]);
  const [viewPost, setViewPost] = useState(null);
  // The calendar entry the preview was opened from (who planned it, for whom).
  const [viewEvent, setViewEvent] = useState(null);
  const viewEventRef = useRef(null);
  const myName = useMyDisplayName();
  const [viewPostId, setViewPostId] = useState(null);
  const [showPreviewPost, setShowPreviewPost] = useState(false);
  const [postFiles, setPostFiles] = useState([]);
  const [showPlanningForm, setShowPlanningForm] = useState();
  const [editDataSchedule, setEditDataSchedule] = useState();
  const [editPost, setEditPost] = useState();
  const [showEditPost, setShowEditPost] = useState();
  const [showAiPlanner, setShowAiPlanner] = useState(false);
  const [recommendation, setRecommendation] = useState(null);
  const [showRecommendation, setShowRecommendation] = useState(false);

  // Set right before viewPostId, so the fetch effect below reads the current value.
  const viewReadOnlyRef = useRef(false);

  // Controlled view. Clicking a post on a day with several posts opens that
  // day's agenda instead of the post; from the agenda a click opens the post.
  const [view, setView] = useState("month");
  const [focusDate, setFocusDate] = useState(null);

  const openDayAgenda = (day) => {
    setFocusDate(moment(day).startOf("day").toDate());
    setView("agenda");
  };

  const handleEventClick = (event, open) => {
    if (view !== "agenda" && event?.start) {
      const sameDay = events.filter((e) => e?.start && moment(e.start).isSame(event.start, "day"));
      if (sameDay.length > 1) {
        openDayAgenda(event.start);
        return;
      }
    }
    open(event);
  };

  useEffect(() => {
    setCalendarEntry(schedules, setEvents, settings?.user?.username ?? null);
  }, [schedules, settings?.user?.username]);

  useEffect(() => {
    if (viewPostId > 0) {
      const fetchViewPost = async () => {
        const data = await crudApi.show(SocialEndpoints.posts.show, viewPostId);
        if (data) {
          setViewPost(data);
          if (viewReadOnlyRef.current) {
            // Someone else's post planned on my account: I can read it, not edit it.
            setShowPreviewPost(true);
          } else {
            setEditPost(data);
            setShowEditPost(true);
          }
        }
      };

      const loadPostDocuments = async () => {
        // A post someone else planned on my account: read its files through the
        // member-gated group routes; the document routes are owner-only.
        const groupId = viewReadOnlyRef.current ? viewEventRef.current?.sharedIn?.[0]?.id : null;
        const response = await crudApi.getAll(
          groupId
            ? MediaplannerGroupEndpoints.endpoints.postFiles + groupId + "/" + viewPostId
            : DocumentsEndpoints.showPostFiles + viewPostId
        );
        if (response.ok) {
          setPostFiles(Array.isArray(response.data) ? response.data : []);
        }
      };

      if (viewPostId != null) {
        fetchViewPost();
        loadPostDocuments();
      }
    }
  }, [viewPostId]);

  const eventStyleGetter = (event) => {
    let borderColor = "#038edc";
    if (event.status.includes("error")) borderColor = "#f34e4e";
    if (event.status.includes("scheduled")) borderColor = "#f1734f";
    if (event.status.includes("published")) borderColor = "#51d28c";

    return {
      style: {
        borderLeft: `4px solid ${borderColor}`,
        backgroundColor: "#fff",
        borderRadius: "8px",
        color: "#111827",
        border: "1px solid #eaecf0",
        padding: "0",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      },
    };
  };

  const scheduleForm = ScheduleForm.createScheduleForm(
    schedules,
    setSchedules,
    null,
    null
  );
  const scheduleFormik = useFormik(scheduleForm);

  const createScheduleWithPost = (start) => {
    let schedule = { ...Schedule };
    schedule.local_user_time = start;
    setEditDataSchedule(schedule);
  };

  const handleSelectSlot = ({ start, end, slots }) => {
    if (!dontUseCalendarClick) {
      const datePart = moment(start).format("YYYY-MM-DD");
      const timePart = moment().format("HH:mm");
      const startDate = `${datePart}T${timePart}`;

      createScheduleWithPost(startDate);
      setShowPlanningForm(true);
    }
  };

  const savePostAndSchedule = async () => {
    let response = await crudApi.createNew(SocialEndpoints.posts.new, {
      draft: false,
    });
    if (response.ok) {
      setEditPost(response.data);
      return response.data.id;
    }
  };

  const SaveSchedule = () => {
    return (
      <Button
        onClick={async (e) => {
          e.preventDefault();
          // Validate before anything is saved: an empty date must highlight the
          // field, not create a post and then fail on the schedule.
          const errors = await scheduleFormik.validateForm();
          if (Object.keys(errors).length > 0) {
            scheduleFormik.setTouched(Object.fromEntries(Object.keys(errors).map((k) => [k, true])), false);
            return;
          }
          const postID = await savePostAndSchedule();
          const newValues = { ...scheduleFormik.values };
          // Calendar planning is always on the own account; the plan-for field
          // is not part of the schedule endpoint's form.
          delete newValues.publish_as;
          newValues.post = { id: postID };
          const response = await crudApi.createNew(
            SocialEndpoints.posts.schedule.new,
            newValues
          );
          listHandler.newToList(response, schedules, setSchedules, null);
          if (response.ok) {
            setShowPlanningForm(false);
            setViewPostId(postID);
          }
        }}
      >
        {t("Backend.Buttons.Save")}
      </Button>
    );
  };

  const customTrigger = async (triggerValue) => {
    //This is needed because the form inverses the trigger Value
    let originalID = triggerValue * -1;
    const event = events.find((obj) => obj.id === originalID);
    if (!event.planning) {
      const response = await crudApi.justGet(
        SocialEndpoints.posts.schedule.show + event.scheduleId
      );
      listHandler.replaceItemInList(response.data, schedules, setSchedules);
    }
    setViewPostId(-1);
  };

  const deletePlan = async (schedule) => {
    const scheduleId = schedule.scheduleId;
    const response = await crudApi.justGet(
      SocialEndpoints.posts.schedule.show + scheduleId
    );
    const deleteResponse = await crudApi.del(
      SocialEndpoints.posts.schedule.delete,
      scheduleId
    );
    if (deleteResponse.ok) {
      const updatedList = listHandler.deleteWithoutCall(
        response.data,
        schedules
      );
      setSchedules([...updatedList]);
    }
  };

  const StartAiPlannerButton = () => {
    return (
      <Button
        onClick={() => setShowAiPlanner(true)}
        style={{ borderRadius: 10, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 8 }}
      >
        <FontAwesomeIcon icon={faWandMagicSparkles} />
        AI Planner
      </Button>
    );
  };

  return (
    <>
      <NewModal
        show={showRecommendation}
        setShow={setShowRecommendation}
        title={recommendation && recommendation.title}
      >
        {recommendation && <>{recommendation.text}</>}
      </NewModal>
      <AiPlanningAssistantView
        show={showAiPlanner}
        setShow={setShowAiPlanner}
        onPlanSaved={onPlanSaved}
      />
      <PostFormView
        showEdit={showEditPost}
        setShowEdit={setShowEditPost}
        editData={editPost}
        trigger={viewPostId}
        setTrigger={customTrigger}
        refreshOnClose={true}
      />
      <ScheduleFormView
        formik={scheduleFormik}
        showPlanningForm={showPlanningForm}
        setshowPlanningForm={setShowPlanningForm}
        editData={editDataSchedule}
        CustomSaveButton={() => <SaveSchedule formik={scheduleFormik} />}
        allPlatforms={allPlatforms}
      />
      <NewModal
        show={showPreviewPost}
        setShow={setShowPreviewPost}
        size="xl"
        noPadding
        title={viewPost?.title}
        ExtraButton={(() => {
          const mine = (viewPost?.schedules ?? []).find((sch) => sch.id === viewEvent?.scheduleId) ?? null;
          if (!mine || mine.approval_status !== "proposed" || !scheduleIsForMe(mine, settings?.user?.username)) return null;
          return () => (
            <DecideButtons
              key={mine.id}
              schedule={mine}
              onDecided={(updated) => {
                setViewPost((prev) => prev ? { ...prev, schedules: (prev.schedules ?? []).map((sch) => (sch.id === updated.id ? { ...sch, ...updated } : sch)) } : prev);
                setViewEvent((prev) => prev ? { ...prev, approvalStatus: updated.approval_status } : prev);
                setSchedules?.((prev) => (prev ?? []).map((sch) => (sch.id === updated.id ? { ...sch, ...updated } : sch)));
              }}
            />
          );
        })()}
        onHide={() => {
          setViewPost(null);
          setViewEvent(null);
          setViewPostId(null);
          setPostFiles(null);
        }}
      >
        {viewPost && (
          <PostPreviewPanel
            post={viewPost}
            author={viewEvent?.delegatedToMe && viewEvent?.plannedByUser
              ? viewEvent.plannedByUser
              : { id: "me", name: myName, username: settings?.user?.username }}
            files={postFiles ?? []}
            binaryEndpoint={viewEvent?.delegatedToMe && viewEvent?.sharedIn?.[0]?.id
              ? MediaplannerGroupEndpoints.endpoints.getImageBinary + viewEvent.sharedIn[0].id + "/"
              : undefined}
          >
            {(() => {
              const mine = (viewPost?.schedules ?? []).find((sch) => sch.id === viewEvent?.scheduleId) ?? null;
              return canWithdraw(mine, settings?.user?.username) ? (
                <WithdrawApprovalButton
                  schedule={mine}
                  size="sm"
                  style={{ alignSelf: "flex-start" }}
                  onDone={(updated) => {
                    setViewPost((prev) => prev ? { ...prev, schedules: (prev.schedules ?? []).map((sch) => (sch.id === updated.id ? { ...sch, ...updated } : sch)) } : prev);
                    setSchedules?.((prev) => (prev ?? []).map((sch) => (sch.id === updated.id ? { ...sch, ...updated } : sch)));
                  }}
                />
              ) : null;
            })()}
          </PostPreviewPanel>
        )}
      </NewModal>
      {!savePlan && (
        <Row className="mb-3">
          <Col>
            <StartAiPlannerButton />
          </Col>
        </Row>
      )}
      <Row>
        <Col>
          <Calendar
            selectable
            onSelectSlot={handleSelectSlot}
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 1000 }}
            date={focusDate ?? date}
            view={view}
            onView={(nextView) => {
              setView(nextView);
              if (nextView !== "agenda") setFocusDate(null);
            }}
            length={view === "agenda" && focusDate ? 1 : 30}
            // Clicking a date number in the month grid: that day's agenda. Without
            // this the library drills into the "day" view while the controlled
            // date stays at the month start, i.e. the wrong day.
            onDrillDown={(day) => openDayAgenda(day)}
            eventPropGetter={eventStyleGetter}
            onNavigate={(newDate) => {
              if (view === "agenda" && focusDate) setFocusDate(moment(newDate).startOf("day").toDate());
              if (onMonthChange) {
                onMonthChange(newDate); // tell wrapper month changed
              }
            }}
            toolbar={true}
            components={{
              event: (props) => (
                <CustomEvent
                  {...props}
                  setViewId={(event) => handleEventClick(event, (ev) => {
                    if (ev.id) {
                      viewReadOnlyRef.current = Boolean(ev.delegatedToMe);
                      viewEventRef.current = ev;
                      setViewEvent(ev);
                      setViewPostId(ev.id);
                    } else {
                      setRecommendation(ev);
                      setShowRecommendation(true);
                    }
                  })}
                  deletePlan={
                    customDeleteFunction ? customDeleteFunction : deletePlan
                  }
                />
              ),
            }}
          />
        </Col>
      </Row>
    </>
  );
};

export default PostCalendar;
